import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  ActionIcon,
  Badge,
  Text,
  Stack,
  Group,
  Divider,
  Button,
  ScrollArea,
  Indicator,
  ThemeIcon,
  Box,
  Tooltip,
  Center,
  Loader,
  UnstyledButton,
  rem,
  useMantineColorScheme
} from '@mantine/core';
import {
  IconBell,
  IconBellRinging,
  IconSettings,
  IconMessage2,
  IconCreditCard,
  IconAlertTriangle,
  IconCheck,
  IconRefresh,
  IconEye,
  IconSparkles,
  IconUsers,
  IconArrowRight
} from '@tabler/icons-react';
import { notifications as mantineNotifications } from '@mantine/notifications';
import { notificationService, type NotificationDTO } from '../services/notificationService';
import { MultijugadorService } from '../services/multijugadorService';
import { authService } from '../services/authService';

interface UiNotification {
  id: number;
  title: string;
  description: string;
  time: string;
  type: 'foro' | 'sistema' | 'examen' | 'pago' | 'alerta';
  read: boolean;
  gradient: string;
  color: string;
  icon: React.ComponentType<{ size?: number }>;
  metadata?: Record<string, any>;
  creador?: {
    id: number;
    username: string;
    nombre?: string;
    apellidos?: string;
    email: string;
  };
}

const NotificationDropdown: React.FC = () => {
  console.log('NotificationDropdown component rendering...');
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const [notifications, setNotifications] = useState<UiNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(false);

  // Simple time formatting function
  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

    if (diffInMinutes < 1) return 'hace un momento';
    if (diffInMinutes < 60) return `hace ${diffInMinutes}m`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `hace ${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `hace ${diffInDays}d`;

    return date.toLocaleDateString();
  };

  const mapDtoToUi = (dto: NotificationDTO): UiNotification => {
    const typeMap = {
      SISTEMA: {
        type: 'sistema' as const,
        gradient: '#64748b',
        color: '#64748b',
        icon: IconSettings
      },
      FORO: {
        type: 'foro' as const,
        gradient: '#3b82f6',
        color: '#3b82f6',
        icon: IconMessage2
      },
      EXAMEN: {
        type: 'examen' as const,
        gradient: '#10b981',
        color: '#10b981',
        icon: IconSparkles
      },
      PAGO: {
        type: 'pago' as const,
        gradient: '#f59e0b',
        color: '#f59e0b',
        icon: IconCreditCard
      },
      ALERTA: {
        type: 'alerta' as const,
        gradient: '#ef4444',
        color: '#ef4444',
        icon: IconAlertTriangle
      },
    };

    let mapped = typeMap[dto.tipo] || typeMap.SISTEMA;

    // Override visual para eventos de amistad: ícono y color más amigables que el de alerta.
    const tipoEvento = dto.metadata?.tipoEvento;
    if (tipoEvento === 'SOLICITUD_AMISTAD_RECIBIDA' || tipoEvento === 'SOLICITUD_AMISTAD_ACEPTADA') {
      mapped = {
        type: mapped.type,
        gradient: '#8b5cf6',
        color: '#8b5cf6',
        icon: IconUsers,
      };
    }

    return {
      id: dto.id,
      title: dto.titulo,
      description: dto.mensaje,
      time: formatTimeAgo(dto.creadoEn),
      read: dto.leida,
      metadata: dto.metadata,
      creador: dto.creador,
      ...mapped
    };
  };

  const loadNotifications = useCallback(async () => {
    try {
      console.log('Loading notifications...');
      setLoading(true);
      const [notificationsData, countData] = await Promise.all([
        notificationService.getMyNotifications(0, 10),
        notificationService.getUnreadCount()
      ]);

      console.log('Notifications loaded:', notificationsData);
      console.log('Unread count:', countData);

      setNotifications(notificationsData.map(mapDtoToUi));
      setUnreadCount(countData);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
      mantineNotifications.show({
        title: 'Error',
        message: 'No se pudieron cargar las notificaciones',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marcando como leída:', error);
      mantineNotifications.show({
        title: 'Error',
        message: 'Error al marcar la notificación como leída',
        color: 'red'
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifications.map(n => notificationService.markAsRead(n.id))
      );

      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);

      mantineNotifications.show({
        title: 'Completado',
        message: `${unreadNotifications.length} notificaciones marcadas como leídas`,
        color: 'green',
      });
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
      mantineNotifications.show({
        title: 'Error',
        message: 'Error al marcar las notificaciones como leídas',
        color: 'red'
      });
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const NotificationItem: React.FC<{ notification: UiNotification }> = ({ notification }) => {
    const Icon = notification.icon;
    const hasRoomInvitation = notification.metadata?.codigoSala;

    const handleJoinRoom = async (e: React.MouseEvent) => {
      e.stopPropagation();
      const codigoSala = notification.metadata!.codigoSala;

      try {
        // Intentar unirse automáticamente
        const sala = await MultijugadorService.unirseASala({ codigoSala });

        // Cerrar el dropdown
        setOpened(false);

        // Navegar a la sala con los datos
        navigate(`/multijugador/sala/${codigoSala}`, {
          state: { sala, fromInvitation: true }
        });

        // Marcar como leída
        if (!notification.read) {
          markAsRead(notification.id);
        }

        mantineNotifications.show({
          title: '¡Te has unido!',
          message: `Te uniste exitosamente a ${sala.nombreSala}`,
          color: 'green',
        });
      } catch (error: any) {
        console.error('Error al unirse a sala:', error);
        mantineNotifications.show({
          title: 'Error',
          message: error.message || 'No se pudo unir a la sala',
          color: 'red',
        });
      }
    };

    return (
      <Box
        style={{
          width: '100%',
          padding: rem(12),
          borderRadius: rem(8),
          transition: 'all 0.2s ease',
          opacity: notification.read ? 0.7 : 1,
        }}
      >
        <UnstyledButton
          onClick={() => {
            if (!notification.read) {
              markAsRead(notification.id);
            }

            // Navegar según el tipo de evento (si la notif lo indica).
            const tipoEvento = notification.metadata?.tipoEvento;
            let destino: string | null = null;
            if (tipoEvento === 'SOLICITUD_AMISTAD_RECIBIDA') {
              destino = '/estudiante/solicitudes?tab=recibidas';
            } else if (tipoEvento === 'SOLICITUD_AMISTAD_ACEPTADA') {
              destino = '/estudiante/solicitudes?tab=amigos';
            } else if (authService.isEstudiante()) {
              // Notificación informativa (bienvenida, clases, pago…): llevar a la
              // página de notificaciones para ver el cuerpo completo del mensaje.
              // (La página de lectura es solo de estudiante; el admin no tiene aún.)
              destino = `/estudiante/notificaciones?n=${notification.id}`;
            }

            if (destino) {
              setOpened(false);
              navigate(destino);
            }
          }}
          style={{ width: '100%', cursor: 'pointer' }}
        >
          <Group align="flex-start" gap="md" wrap="nowrap">
            <Indicator
              disabled={notification.read}
              color={notification.color}
              size={8}
              offset={-2}
            >
              <ThemeIcon
                size="lg"
                radius="md"
                style={{
                  background: notification.gradient,
                  border: 'none'
                }}
              >
                <Icon size={16} />
              </ThemeIcon>
            </Indicator>

            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text
                  fw={notification.read ? 400 : 600}
                  size="sm"
                  lineClamp={1}
                  style={{
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                    flex: 1
                  }}
                >
                  {notification.title}
                </Text>
                <Text
                  size="xs"
                  style={{
                    color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {notification.time}
                </Text>
              </Group>

              <Text
                size="xs"
                lineClamp={2}
                style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550' }}
              >
                {notification.description}
              </Text>
            </Stack>
          </Group>
        </UnstyledButton>

        {/* Botón para unirse a sala si es una invitación */}
        {hasRoomInvitation && (
          <Button
            variant="light"
            color="blue"
            size="xs"
            leftSection={<IconUsers size={14} />}
            rightSection={<IconArrowRight size={14} />}
            mt="xs"
            fullWidth
            onClick={handleJoinRoom}
          >
            Unirse a la Sala
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Menu
      opened={opened}
      onChange={(isOpened) => {
        console.log('Menu toggled:', isOpened);
        setOpened(isOpened);
      }}
      position="bottom-end"
      width={380}
      shadow="xl"
      radius="md"
      offset={8}
      onOpen={() => {
        console.log('Menu opened, loading notifications...');
        loadNotifications();
      }}
    >
      <Menu.Target>
        <Indicator
          disabled={unreadCount === 0}
          label={unreadCount > 99 ? '99+' : unreadCount}
          size={16}
          color="red"
          offset={4}
        >
          <ActionIcon
            variant="subtle"
            size={36}
            onClick={() => {
              console.log('ActionIcon clicked - opening menu');
              setOpened(!opened);
            }}
            style={{
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(40px) saturate(200%)',
              WebkitBackdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${
                colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'
              }`,
              borderRadius: '50%',
              color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
              boxShadow: colorScheme === 'dark'
                ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
            }}
          >
            {unreadCount > 0 ? (
              <IconBellRinging
                size={18}
                style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550' }}
              />
            ) : (
              <IconBell
                size={18}
                style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#5a5550' }}
              />
            )}
          </ActionIcon>
        </Indicator>
      </Menu.Target>

      <Menu.Dropdown
        style={{
          background: colorScheme === 'dark'
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.9))'
            : 'linear-gradient(135deg, rgba(247, 243, 238, 0.95), rgba(242, 237, 230, 0.9))',
          backdropFilter: 'blur(40px) saturate(200%)',
          WebkitBackdropFilter: 'blur(40px) saturate(200%)',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
          boxShadow: colorScheme === 'dark'
            ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
            : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
          padding: 0,
        }}
      >
        {/* Header */}
        <Box p="md" style={{ borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}` }}>
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <IconBell size={18} style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }} />
              <Text fw={600} size="sm" style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26' }}>
                Notificaciones
              </Text>
              {unreadCount > 0 && (
                <Badge size="xs" variant="filled" color="red">
                  {unreadCount}
                </Badge>
              )}
            </Group>

            <Group gap="xs">
              <Tooltip label="Actualizar">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={loadNotifications}
                  loading={loading}
                >
                  <IconRefresh size={14} />
                </ActionIcon>
              </Tooltip>

              {unreadCount > 0 && (
                <Tooltip label="Marcar todas como leídas">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={markAllAsRead}
                    style={{ color: '#22c55e' }}
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>
        </Box>

        {/* Content */}
        {loading ? (
          <Center p="xl">
            <Loader size="sm" />
          </Center>
        ) : notifications.length === 0 ? (
          <Center p="xl">
            <Stack align="center" gap="xs">
              <ThemeIcon
                size={40}
                radius="xl"
                variant="light"
                color="gray"
              >
                <IconBell size={20} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" ta="center">
                No tienes notificaciones
              </Text>
            </Stack>
          </Center>
        ) : (
          <ScrollArea h={400} type="scroll">
            <Stack gap={0} p="xs">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                />
              ))}
            </Stack>
          </ScrollArea>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Divider />
            {authService.isEstudiante() && (
              <Box p="md">
                <Button
                  variant="subtle"
                  fullWidth
                  size="sm"
                  onClick={() => {
                    setOpened(false);
                    // Navegar a la página completa de notificaciones del estudiante
                    navigate('/estudiante/notificaciones');
                  }}
                  leftSection={<IconEye size={14} />}
                  styles={{
                    root: {
                      color: '#000000',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                      }
                    }
                  }}
                >
                  Ver todas las notificaciones
                </Button>
              </Box>
            )}
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default NotificationDropdown;